import { notify, useMyTasks, useTeam, useAccess, type MyTask } from '@church/data'
import {
  faArrowRight,
  faArrowRotateLeft,
  faBell,
  faCalendarXmark,
  faCheck,
  faPlus,
  faSnooze,
} from '@church/ui-icons'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useId, useState } from 'react'
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorNote,
  Field,
  Icon,
  IconButton,
  Input,
  ListHeader,
  PageHeader,
  PanelSection,
  Select,
  QueryState,
  SidePanel,
  Textarea,
} from '../components/ui.tsx'
import { PageAction } from '../components/PageAction.tsx'
import { pressableRow } from '../components/row-press.ts'
import { DisplayOptions } from '../components/DisplayOptions.tsx'
import { useListDisplay } from '../lib/display.ts'

/**
 * My Tasks: the follow-ups workflows (and colleagues) have handed me. Each
 * row is the task, who it is about and when; ticking it off happens right
 * there, and everything else — snoozing, handing it on, notes — in its panel.
 */
export const Route = createFileRoute('/_authed/_shell/tasks')({
  component: TasksScreen,
})

const prettyDate = (date: string) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })

const todayIso = () => new Date().toISOString().slice(0, 10)

/** What a task's row shows on a phone; everything shows from `sm` up. */
const ROW_FIELDS = [
  { id: 'about', label: 'Who it is about', default: true },
  { id: 'notes', label: 'Notes', default: false },
  { id: 'due', label: 'Due date', default: true },
] as const
type RowField = (typeof ROW_FIELDS)[number]['id']

function TasksScreen() {
  const tasks = useMyTasks()
  const [adding, setAdding] = useState(false)
  // Which task's panel is open; `shown` outlives closing so it can slide away.
  const [openId, setOpenId] = useState<string | null>(null)
  const [shownId, setShownId] = useState<string | null>(null)
  const display = useListDisplay('tasks', ROW_FIELDS)
  const open = [...tasks.overdue, ...tasks.upcoming, ...tasks.snoozed]
  const shown = open.find((task) => task.id === shownId)
  const choose = (id: string) => {
    setShownId(id)
    setOpenId(id)
  }

  return (
    <div className="max-w-3xl space-y-6">
      <PageHeader
        title="My tasks"
        action={
          <div className="flex items-center gap-1">
            <DisplayOptions fields={ROW_FIELDS} shown={display.shown} onChange={display.set} />
            <PageAction icon={faPlus} label="New task" onClick={() => setAdding(true)} />
          </div>
        }
      />
      <QueryState
        query={tasks.query}
        safe="Nothing was changed: your tasks are safe on the server."
      >
        {() => (
          <>
            {open.length === 0 && (
              <Card className="p-2">
                <EmptyState
                  title={tasks.done.length > 0 ? 'All done' : 'Nothing waiting'}
                  hint="Tasks from workflows land here — a guest to call, a giver to thank."
                  action={
                    <Button variant="secondary" icon={faPlus} onClick={() => setAdding(true)}>
                      New task
                    </Button>
                  }
                />
              </Card>
            )}
            {(open.length > 0 || tasks.done.length > 0) && (
              <Card bleed>
                {[
                  { title: 'Overdue', list: tasks.overdue },
                  { title: 'To do', list: tasks.upcoming },
                  { title: 'Snoozed', list: tasks.snoozed },
                ].map((section) => (
                  <Section
                    key={section.title}
                    title={section.title}
                    tasks={section.list}
                    actions={tasks}
                    hide={display.hide}
                    onOpen={choose}
                  />
                ))}
                {tasks.done.length > 0 && (
                  <>
                    <ListHeader>Done · {tasks.done.length}</ListHeader>
                    <ul className="divide-y divide-border-subtle">
                      {tasks.done.map((task) => (
                        <li
                          key={task.id}
                          className="page-in flex items-center justify-between gap-2 py-1 pr-2 pl-4"
                        >
                          <span className="truncate text-sm text-content-subtle line-through">
                            {task.title}
                          </span>
                          <IconButton
                            icon={faArrowRotateLeft}
                            label={`Reopen ${task.title}`}
                            onClick={() => tasks.reopen(task.id)}
                          />
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </Card>
            )}
          </>
        )}
      </QueryState>

      <SidePanel
        open={openId !== null && shown !== undefined}
        onOpenChange={(next) => {
          if (!next) setOpenId(null)
        }}
        title={shown?.title ?? ''}
        description={shown ? whenText(shown) : undefined}
        primary={
          shown && (
            <Button
              icon={faCheck}
              className="py-1.5"
              onClick={() => {
                tasks.complete(shown.id)
                setOpenId(null)
              }}
            >
              Mark done
            </Button>
          )
        }
      >
        {shown && (
          <TaskDetails key={shown.id} task={shown} actions={tasks} onDone={() => setOpenId(null)} />
        )}
      </SidePanel>
      <AddTaskPanel open={adding} onClose={() => setAdding(false)} actions={tasks} />
    </div>
  )
}

/** "Due Tue, Oct 20", "Snoozed until Mon, Oct 26" — or nothing at all. */
function whenText(task: MyTask): string {
  if (task.snoozed && task.snoozedUntil) return `Snoozed until ${prettyDate(task.snoozedUntil)}`
  if (task.dueOn) return `${task.overdue ? 'Was due' : 'Due'} ${prettyDate(task.dueOn)}`
  return 'No due date'
}

function Section({
  title,
  tasks,
  actions,
  hide,
  onOpen,
}: {
  title: string
  tasks: MyTask[]
  actions: ReturnType<typeof useMyTasks>
  hide: (field: RowField) => string
  onOpen: (id: string) => void
}) {
  const [leaving, setLeaving] = useState<ReadonlySet<string>>(new Set())
  if (tasks.length === 0) return null
  return (
    <>
      <ListHeader>
        {title} · {tasks.length}
      </ListHeader>
      <div className="divide-y divide-border-subtle">
        {tasks.map((task) => (
          <div
            key={task.id}
            className={`flex items-center transition-opacity duration-(--duration-base) ease-out ${
              leaving.has(task.id) ? 'opacity-40' : ''
            }`}
          >
            {/* Ticking it off is the point of the list: it stays one tap away.
                The tick shows, the row dims, then it moves to Done. */}
            <label className="flex shrink-0 items-center self-stretch py-3 pr-2 pl-4">
              <input
                type="checkbox"
                aria-label={`Complete ${task.title}`}
                className="size-5 accent-brand-solid"
                checked={leaving.has(task.id)}
                onChange={() => {
                  setLeaving((was) => new Set(was).add(task.id))
                  window.setTimeout(() => {
                    actions.complete(task.id)
                    setLeaving((was) => {
                      const next = new Set(was)
                      next.delete(task.id)
                      return next
                    })
                  }, 350)
                }}
              />
            </label>
            <button
              type="button"
              onClick={() => onOpen(task.id)}
              className={`flex min-w-0 flex-1 items-center gap-3 py-3 pr-4 pl-2 text-left ${pressableRow} ${
                leaving.has(task.id) ? 'line-through' : ''
              }`}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-base font-medium text-content-default">
                  {task.title}
                </span>
                {task.personName && (
                  <span className={`block truncate text-sm text-content-muted ${hide('about')}`}>
                    {task.personName}
                  </span>
                )}
                {task.notes && (
                  <span className={`block truncate text-sm text-content-subtle ${hide('notes')}`}>
                    {task.notes}
                  </span>
                )}
              </span>
              <span className={hide('due')}>
                {task.snoozed && task.snoozedUntil ? (
                  <Chip>
                    <Icon icon={faSnooze} className="size-3" />
                    {prettyDate(task.snoozedUntil)}
                  </Chip>
                ) : task.dueOn ? (
                  <Chip tone={task.overdue ? 'danger' : 'neutral'}>{prettyDate(task.dueOn)}</Chip>
                ) : null}
              </span>
            </button>
          </div>
        ))}
      </div>
    </>
  )
}

/** One task's panel: who it is about, its due date, snoozing, handing it on, notes. */
function TaskDetails({
  task,
  actions,
  onDone,
}: {
  task: MyTask
  actions: ReturnType<typeof useMyTasks>
  onDone: () => void
}) {
  const access = useAccess()
  const team = useTeam()
  const [notes, setNotes] = useState(task.notes ?? '')
  const [until, setUntil] = useState('')

  return (
    <>
      {task.personId && task.personName && (
        <PanelSection title="About">
          <Link
            to="/people/$id"
            params={{ id: task.personId }}
            className="flex items-center justify-between rounded-lg px-1 py-2 text-base font-medium text-content-brand hover:bg-surface-sunken"
          >
            {task.personName}
            <Icon icon={faArrowRight} />
          </Link>
        </PanelSection>
      )}

      <PanelSection title="Due" hint="When it should be done. Past it, the task shows as overdue.">
        <div className="flex gap-2">
          <Input
            type="date"
            aria-label="Due date"
            value={task.dueOn ?? ''}
            onChange={(event) => actions.setDue(task.id, event.target.value || null)}
          />
          {task.dueOn && (
            <IconButton
              icon={faCalendarXmark}
              label="No due date"
              variant="secondary"
              onClick={() => actions.setDue(task.id, null)}
            />
          )}
        </div>
      </PanelSection>

      <PanelSection
        title="Snooze"
        hint="A snoozed task waits under Snoozed and comes back on the day."
      >
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => actions.snooze(task.id, 1)}>
            Tomorrow
          </Button>
          <Button variant="secondary" onClick={() => actions.snooze(task.id, 7)}>
            Next week
          </Button>
          {task.snoozed && (
            <Button
              variant="ghost"
              icon={faBell}
              onClick={() => actions.snoozeUntil(task.id, null)}
            >
              Wake it now
            </Button>
          )}
        </div>
        <form
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            if (until) actions.snoozeUntil(task.id, until)
            setUntil('')
          }}
        >
          <Input
            type="date"
            aria-label="Snooze until"
            min={todayIso()}
            value={until}
            onChange={(event) => setUntil(event.target.value)}
          />
          <Button
            type="submit"
            variant="secondary"
            icon={faSnooze}
            className="shrink-0 whitespace-nowrap"
            disabled={!until}
          >
            Until then
          </Button>
        </form>
      </PanelSection>

      {access.has('messages', 'viewer') && (team.data?.length ?? 0) > 0 && (
        <PanelSection title="Hand it on" hint="It leaves your list and lands on theirs.">
          <Select
            aria-label="Reassign to"
            value=""
            onChange={(event) => {
              if (!event.target.value) return
              actions.reassign(
                task.id,
                event.target.value,
                team.data?.find((m) => m.userId === event.target.value)?.name,
              )
              onDone()
            }}
          >
            <option value="">Reassign to…</option>
            {(team.data ?? []).map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.name}
              </option>
            ))}
          </Select>
        </PanelSection>
      )}

      <PanelSection title="Notes">
        <Textarea
          rows={4}
          aria-label="Notes"
          placeholder="What happened, what is next…"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          onBlur={() => {
            const next = notes.trim() || null
            if (next !== task.notes) actions.annotate(task.id, next)
          }}
        />
      </PanelSection>
    </>
  )
}

function AddTaskPanel({
  open,
  onClose,
  actions,
}: {
  open: boolean
  onClose: () => void
  actions: ReturnType<typeof useMyTasks>
}) {
  const formId = useId()
  const [title, setTitle] = useState('')
  const [due, setDue] = useState('')
  const [notes, setNotes] = useState('')

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title="Add a task"
      primary={
        <Button
          type="submit"
          form={formId}
          icon={faPlus}
          className="py-1.5"
          disabled={!title.trim()}
          pending={actions.create.isPending}
          pendingLabel="Adding…"
        >
          Add task
        </Button>
      }
    >
      <form
        id={formId}
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          actions.create.mutate(
            { title: title.trim(), dueOn: due || null, notes: notes.trim() || null },
            {
              onSuccess: () => {
                notify({
                  title: `Added: ${title.trim()}`,
                  description: due ? `Due ${prettyDate(due)}.` : 'It’s on your list.',
                })
                setTitle('')
                setDue('')
                setNotes('')
                onClose()
              },
            },
          )
        }}
      >
        <Field label="Task">
          <Input
            value={title}
            placeholder="Call Ada about the welcome lunch"
            onChange={(event) => setTitle(event.target.value)}
            required
          />
        </Field>
        <Field label="Due">
          <Input type="date" value={due} onChange={(event) => setDue(event.target.value)} />
        </Field>
        <Field label="Notes">
          <Textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </Field>
        <ErrorNote error={actions.create.error} />
      </form>
    </SidePanel>
  )
}
